import React from 'react';
import { Button } from '../../../components/ui/Button';
import { InvoiceStatusBadge } from './InvoiceStatusBadge';
import type { Invoice } from '../../../core/models/Invoice';
import { format } from 'date-fns';
import { Download, Mail, Printer } from 'lucide-react';

interface InvoicePreviewProps {
  invoice: Invoice;
  clientName?: string;
  organizationName?: string;
  onPrint?: () => void;
  onEmail?: () => void;
  onDownload?: () => void;
}

export const InvoicePreview: React.FC<InvoicePreviewProps> = ({
  invoice,
  clientName,
  organizationName,
  onPrint,
  onEmail,
  onDownload,
}) => {
  return (
    <div className="mx-auto max-w-4xl rounded-lg border bg-white p-4 sm:p-8">
      {/* Header */}
      <div className="flex justify-between items-start mb-8 border-b pb-6">
        <div>
          <h1 className="text-2xl font-bold mb-2">INVOICE</h1>
          {organizationName && (
            <p className="text-gray-600">{organizationName}</p>
          )}
        </div>
        <div className="text-right">
          <p className="text-sm text-gray-600 mb-1">Invoice #</p>
          <p className="font-semibold text-lg">{invoice.number}</p>
          <div className="mt-2">
            <InvoiceStatusBadge status={invoice.status} />
          </div>
        </div>
      </div>

      {/* Invoice Details */}
      <div className="mb-8 grid grid-cols-1 gap-6 sm:grid-cols-2 sm:gap-8">
        <div>
          <h3 className="font-semibold mb-2">Bill To:</h3>
          <p className="text-gray-700">{clientName || 'Client'}</p>
        </div>
        <div className="text-left sm:text-right">
          <div className="space-y-1 text-sm">
            <p>
              <span className="text-gray-600">Date:</span>{' '}
              <span className="font-medium">{format(new Date(invoice.date), 'PP')}</span>
            </p>
            <p>
              <span className="text-gray-600">Due Date:</span>{' '}
              <span className="font-medium">{format(new Date(invoice.dueDate), 'PP')}</span>
            </p>
          </div>
        </div>
      </div>

      {/* Items Table */}
      <div className="mb-8 overflow-x-auto">
        <table className="w-full min-w-[40rem]">
          <thead className="bg-gray-50">
            <tr>
              <th className="px-4 py-3 text-left text-sm font-medium text-gray-700">Description</th>
              <th className="px-4 py-3 text-right text-sm font-medium text-gray-700">Quantity</th>
              <th className="px-4 py-3 text-right text-sm font-medium text-gray-700">Rate</th>
              <th className="px-4 py-3 text-right text-sm font-medium text-gray-700">Amount</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-200">
            {invoice.items.map((item, index) => (
              <tr key={index}>
                <td className="px-4 py-3">{item.description}</td>
                <td className="px-4 py-3 text-right">{item.quantity}</td>
                <td className="px-4 py-3 text-right">${item.rate.toFixed(2)}</td>
                <td className="px-4 py-3 text-right font-medium">${item.amount.toFixed(2)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* Totals */}
      <div className="mb-8 flex justify-end">
        <div className="w-64 space-y-2">
          <div className="flex justify-between text-sm">
            <span className="text-gray-600">Subtotal:</span>
            <span className="font-medium">${invoice.subtotal.toFixed(2)}</span>
          </div>
          <div className="flex justify-between text-sm">
            <span className="text-gray-600">Tax:</span>
            <span className="font-medium">${invoice.tax.toFixed(2)}</span>
          </div>
          <div className="flex justify-between text-lg font-semibold border-t pt-2">
            <span>Total:</span>
            <span>${invoice.total.toFixed(2)}</span>
          </div>
        </div>
      </div>

      {/* Notes & Terms */}
      {invoice.notes && (
        <div className="mb-4">
          <h3 className="font-semibold mb-2">Notes:</h3>
          <p className="text-gray-700">{invoice.notes}</p>
        </div>
      )}

      {invoice.terms && (
        <div className="mb-8">
          <h3 className="font-semibold mb-2">Terms & Conditions:</h3>
          <p className="text-gray-700 text-sm">{invoice.terms}</p>
        </div>
      )}

      {/* Actions */}
      <div className="flex flex-wrap justify-end gap-2 border-t pt-4">
        {onPrint && (
          <Button variant="outline" onClick={onPrint}>
            <Printer className="mr-2 h-4 w-4" />
            Print
          </Button>
        )}
        {onEmail && (
          <Button variant="outline" onClick={onEmail}>
            <Mail className="mr-2 h-4 w-4" />
            Email
          </Button>
        )}
        {onDownload && (
          <Button variant="outline" onClick={onDownload}>
            <Download className="mr-2 h-4 w-4" />
            Download PDF
          </Button>
        )}
      </div>
    </div>
  );
};
